// Estado y reglas del cobro de UNA cuenta. Portado de la Caja anterior sin
// cambiar reglas: el corte de caja cuadra contra lo que se registra aquí
// (multi-método, propina por método, descuento por producto o por cuenta).
import { useCallback, useRef, useState } from 'react';
import { cajaService } from '../../services/api';
import { abrirTicket, TicketData } from '../../utils/ticket';

export type Seleccion =
  | { tipo: 'mesa'; mesaId: string; subcuenta: string }
  | { tipo: 'llevar'; comandaId: string };

export interface Metodo { value: string; label: string }

const num = (v: string | undefined) => parseFloat(v || '0') || 0;

export function useCobro(metodos: Metodo[], config: Record<string, string> | null) {
  const [sel, setSel] = useState<Seleccion | null>(null);
  const [cuenta, setCuenta] = useState<any>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  const [montos, setMontos] = useState<Record<string, string>>({});
  const [propinas, setPropinas] = useState<Record<string, string>>({});
  const [autorizadoPor, setAutorizadoPor] = useState<string | null>(null);
  const [descTipo, setDescTipo] = useState<'porcentaje' | 'monto' | null>(null);
  const [descValor, setDescValor] = useState('');
  const [descItems, setDescItems] = useState<Record<string, string>>({});
  const [pagando, setPagando] = useState(false);
  const [pointPendiente, setPointPendiente] = useState(false);
  const [pagoExitoso, setPagoExitoso] = useState<any>(null);
  const selRef = useRef<Seleccion | null>(null);

  const reiniciarCobro = () => {
    setMontos({}); setPropinas({}); setAutorizadoPor(null);
    setDescTipo(null); setDescValor(''); setDescItems({}); setError('');
  };

  const pedir = (s: Seleccion) => s.tipo === 'mesa'
    ? cajaService.getCuenta(s.mesaId, s.subcuenta)
    : cajaService.getCuentaPorComanda(s.comandaId);

  const abrir = useCallback(async (s: Seleccion) => {
    selRef.current = s;
    setSel(s); setCuenta(null); setPagoExitoso(null); reiniciarCobro(); setCargando(true);
    try {
      const r = await pedir(s);
      if (selRef.current === s) setCuenta(r.data);
    } catch (e: any) {
      setError(e.response?.data?.error || 'No se pudo abrir la cuenta.');
    } finally { setCargando(false); }
  }, []);

  const recargar = useCallback(async () => {
    const s = selRef.current;
    if (!s) return;
    try { setCuenta((await pedir(s)).data); } catch { /* la lista se refresca aparte */ }
  }, []);

  const cerrar = () => { selRef.current = null; setSel(null); setCuenta(null); reiniciarCobro(); };

  /* ─── Descuentos ─── */
  const totalDescItems = Object.values(descItems).reduce((s, v) => s + num(v), 0);
  const hayDescItems = totalDescItems > 0;
  const montoDescuento = (() => {
    if (!cuenta || !autorizadoPor) return 0;
    // Producto y cuenta completa son excluyentes: el pago guarda un solo descuento_tipo.
    if (hayDescItems) return Math.min(totalDescItems, cuenta.total);
    if (!descTipo || !descValor) return 0;
    const v = num(descValor);
    return Math.min(descTipo === 'porcentaje' ? cuenta.total * v / 100 : v, cuenta.total);
  })();
  const totalConDescuento = cuenta ? Math.max(0, cuenta.total - montoDescuento) : 0;
  // Ajustes › Cobros › Redondear totales (el servidor aplica la misma regla al registrar el pago).
  const totalACobrar = config?.caja_redondeo === 'true' ? Math.round(totalConDescuento) : totalConDescuento;
  const redondeo = +(totalACobrar - totalConDescuento).toFixed(2);

  // Nunca más que el importe del renglón: 100% queda exactamente en cortesía.
  const fijarDescItem = (itemId: string, importe: number, monto: number) => {
    const v = Math.max(0, Math.min(monto, importe));
    setDescItems(d => {
      const n = { ...d };
      if (v <= 0) delete n[itemId]; else n[itemId] = v.toFixed(2);
      return n;
    });
    if (v > 0) { setDescTipo(null); setDescValor(''); }
  };
  const descuentoCuenta = (tipo: 'porcentaje' | 'monto', valor: string) => {
    setDescItems({}); setDescTipo(tipo); setDescValor(valor);
  };
  const quitarDescuentos = () => { setDescItems({}); setDescTipo(null); setDescValor(''); };
  const itemsDescuento = () => Object.entries(descItems)
    .filter(([, v]) => num(v) > 0).map(([item_id, m]) => ({ item_id, descuento_monto: num(m) }));

  /* ─── Pago ─── */
  const ingresado = metodos.reduce((s, m) => s + num(montos[m.value]), 0);
  const totalPropina = metodos.reduce((s, m) => s + num(propinas[m.value]), 0);
  const suficiente = ingresado >= totalACobrar - 0.01;
  const faltante = Math.max(0, totalACobrar - ingresado);
  // La propina puede venir aparte (tarjeta paga la cuenta, propina en efectivo).
  const cambio = Math.max(0, ingresado - totalACobrar - totalPropina);
  const puedeCobrar = !!cuenta && !pagando && !pointPendiente && ingresado > 0 && suficiente;

  // % sugerido de propina: al método con importe, o repartido si hay varios.
  const aplicarPctPropina = (pct: number) => {
    const total = +(totalConDescuento * pct / 100).toFixed(2);
    const activos = metodos.filter(m => num(montos[m.value]) > 0);
    if (activos.length <= 1) { setPropinas({ [(activos[0] || metodos[0])?.value || 'efectivo']: total.toFixed(2) }); return; }
    const base = activos.reduce((s, m) => s + num(montos[m.value]), 0);
    const n: Record<string, string> = {};
    activos.forEach((m, i) => {
      n[m.value] = i === activos.length - 1
        ? (total - Object.values(n).reduce((s, v) => s + num(v), 0)).toFixed(2)
        : (num(montos[m.value]) / base * total).toFixed(2);
    });
    setPropinas(n);
  };

  const pagar = async () => {
    if (!cuenta || pagando || pointPendiente) return;
    const activos = metodos.filter(m => num(montos[m.value]) > 0);
    if (!activos.length) { setError('Captura el importe en al menos un método de pago.'); return; }
    if (!suficiente) { setError('El importe no cubre la cuenta.'); return; }
    setPagando(true); setError('');
    try {
      const desc = itemsDescuento();
      const tipoFinal = autorizadoPor ? (desc.length ? 'item' : descTipo) : null;
      const [m1, m2] = activos;
      // La propina se registra en el método donde se capturó (el de mayor monto).
      const propinaMetodo = metodos.map(m => ({ v: m.value, a: num(propinas[m.value]) }))
        .filter(x => x.a > 0).sort((a, b) => b.a - a.a)[0]?.v || null;
      const r = await cajaService.registrarPago({
        comanda_id: cuenta.comanda_id,
        subcuenta_id: sel?.tipo === 'mesa' ? sel.subcuenta : null,
        metodo: m1.value,
        monto_recibido: ingresado,
        propina: totalPropina,
        propina_metodo: propinaMetodo,
        descuento_tipo: tipoFinal,
        descuento_valor: tipoFinal === 'item' ? 0 : (autorizadoPor ? num(descValor) : 0),
        descuento_items: autorizadoPor ? desc : [],
        metodo_2: m2?.value ?? null,
        monto_metodo_1: m2 ? num(montos[m1.value]) : null,
        monto_metodo_2: m2 ? num(montos[m2.value]) : null,
      });
      setPagoExitoso(r.data);
      cerrar();
    } catch (e: any) {
      setError(e.response?.data?.error || 'No se pudo registrar el pago.');
    } finally { setPagando(false); }
  };

  // Impresora del backend; si no hay, diálogo del navegador con el ticket.
  const precuenta = async (): Promise<'ok' | 'navegador'> => {
    if (!cuenta) return 'ok';
    try {
      const r = await cajaService.imprimirPrecuenta(cuenta.comanda_id, sel?.tipo === 'mesa' ? sel.subcuenta : null, autorizadoPor ? itemsDescuento() : [],
        autorizadoPor && !hayDescItems && descTipo && num(descValor) > 0 ? { descuento_tipo: descTipo, descuento_valor: num(descValor) } : null);
      if (r.data?.ok) return 'ok';
      if (r.data?.ticket) { await abrirTicket(r.data.ticket); return 'navegador'; }
    } catch { /* se arma localmente abajo */ }
    const ticket: TicketData = {
      ...(config || {}), precuenta: true,
      mesa_numero: cuenta.etiqueta_cuenta ?? cuenta.mesa_numero ?? null,
      tipo: cuenta.tipo, cliente_nombre: cuenta.cliente_nombre, mesero: cuenta.mesero,
      items: cuenta.items.map((i: any) => ({
        producto: i.producto, cantidad: i.cantidad, precio_unitario: i.importe / i.cantidad, importe: i.importe,
        descuento_monto: autorizadoPor ? num(descItems[i.id]) : 0, notas: i.notas,
      })),
      subtotal: cuenta.subtotal, iva: cuenta.iva, total: totalACobrar, redondeo,
      descuento_tipo: hayDescItems ? 'item' : descTipo, descuento_valor: num(descValor), descuento_monto: montoDescuento,
      metodo: 'efectivo', monto_recibido: 0, propina: 0, cambio: 0, fecha: new Date().toISOString(),
    } as TicketData;
    await abrirTicket(ticket);
    return 'navegador';
  };

  return {
    sel, cuenta, cargando, error, setError, abrir, recargar, cerrar,
    montos, setMonto: (m: string, v: string) => setMontos(p => ({ ...p, [m]: v })), setMontos,
    propinas, setPropina: (m: string, v: string) => setPropinas(p => ({ ...p, [m]: v })), quitarPropina: () => setPropinas({}), aplicarPctPropina,
    autorizadoPor, autorizar: setAutorizadoPor,
    descTipo, descValor, descItems, hayDescItems, montoDescuento, totalConDescuento, totalACobrar, redondeo,
    fijarDescItem, descuentoCuenta, quitarDescuentos,
    ingresado, totalPropina, suficiente, faltante, cambio, puedeCobrar, pagando, pagar,
    pagoExitoso, setPagoExitoso, precuenta,
    pointPendiente, setPointPendiente,
  };
}

export type Cobro = ReturnType<typeof useCobro>;
